import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import type { Company } from '@aviaconsole/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { useToast } from '../components/Toast';
import { Button, Card, Input, PageHeader } from '../components/ui';

type Form = { name: string; shortName: string; slug: string; senderEmail: string; replyToEmail: string; contactName: string; country: string; invoicePrefix: string };

/** "Hees Aviation Services B.V." → "hees-aviation-services-bv" */
const slugify = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50);

/**
 * Global-admin form that creates a company. Rendered as the first-run screen when
 * the platform has no companies yet (`first`), and as a page from the switcher.
 */
export function CompanyNew({ first }: { first?: boolean }) {
  const { user, refresh, setCompany, logout } = useAuth();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const [f, setF] = useState<Form>({ name: '', shortName: '', slug: '', senderEmail: '', replyToEmail: '', contactName: user?.name ?? '', country: 'NL', invoicePrefix: 'INV' });
  const [slugTouched, setSlugTouched] = useState(false);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));

  const create = useMutation({
    mutationFn: () => api<Company>('/api/admin/companies', { method: 'POST', json: {
      name: f.name, shortName: f.shortName || f.name, slug: f.slug, senderEmail: f.senderEmail, country: f.country, invoicePrefix: f.invoicePrefix || 'INV',
      replyToEmail: f.replyToEmail || null, contactName: f.contactName || null,
    } }),
    onSuccess: async (c) => {
      await refresh();
      setCompany(c.id);
      toast.push(t('companyCreated'));
      navigate('/settings');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const form = (
    <form className="stack" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
      <Input label={t('legalName')} required value={f.name} onChange={(e) => { set('name', e.target.value); if (!slugTouched) set('slug', slugify(e.target.value)); }} />
      <div className="grid fields" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <Input label={t('shortName')} placeholder={f.name} value={f.shortName} onChange={(e) => set('shortName', e.target.value)} />
        <Input label={t('slug')} required pattern="[a-z0-9][a-z0-9-]*[a-z0-9]" value={f.slug} onChange={(e) => { setSlugTouched(true); set('slug', e.target.value.toLowerCase()); }} />
      </div>
      <p className="small muted" style={{ margin: '-0.4rem 0 0' }}>{t('shortNameHint')} · {t('slugHint')}</p>
      <div className="grid fields" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <Input label={t('senderAddress')} type="email" required value={f.senderEmail} onChange={(e) => set('senderEmail', e.target.value)} />
        <Input label={t('replyTo')} type="email" value={f.replyToEmail} onChange={(e) => set('replyToEmail', e.target.value)} />
      </div>
      <div className="grid fields" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
        <Input label={t('companyContact')} value={f.contactName} onChange={(e) => set('contactName', e.target.value)} />
        <Input label={t('country')} maxLength={2} value={f.country} onChange={(e) => set('country', e.target.value.toUpperCase())} />
        <Input label={t('invoiceNumbering')} value={f.invoicePrefix} onChange={(e) => set('invoicePrefix', e.target.value)} />
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
        {first
          ? <Button type="button" onClick={() => void logout()} icon="fas fa-right-from-bracket">{t('signOut')}</Button>
          : <Button type="button" onClick={() => navigate(-1)}>{t('cancel')}</Button>}
        <Button type="submit" variant="primary" icon="fas fa-building" disabled={create.isPending}>{t('createCompany')}</Button>
      </div>
    </form>
  );

  if (first) {
    return (
      <div className="login">
        <div className="card pad" style={{ width: 'min(640px, 100%)', textAlign: 'left' }}>
          <div className="wordmark" style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}><span className="a">Avia</span><span className="b">Console</span></div>
          <h2 style={{ marginBottom: '0.3rem' }}>{t('firstCompanyTitle')}</h2>
          <p className="secondary" style={{ margin: '0 0 1.2rem', fontSize: '0.86rem', textWrap: 'pretty' }}>{t('firstCompanyHint')}</p>
          {form}
        </div>
      </div>
    );
  }
  return (
    <>
      <PageHeader crumb={t('newCompanyCrumb')} title={t('newCompany')} sub={t('newCompanySub')} />
      <Card>{form}</Card>
    </>
  );
}
